import { useMemo } from "react";
import { useParams } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Chip, Container, Typography } from "@mui/material";
import useWPPage from "../hooks/useWPPage";
import useLanguage from "../hooks/useLanguage";
import Loading from "../components/Shared/Loading";
import NotFound from "../components/Shared/NotFound";
import SeoHead from "../components/Shared/SeoHead";
import ContactForm, { isContactPage } from "../components/Shared/ContactForm";
import NewsletterSignup, { isNewsletterPage } from "../components/Shared/NewsletterSignup";
import { getContentVisibilityLevel, isContentVisibleForAccess } from "../content/contentClient";

export default function DynamicPage() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const { page, loading, notFound } = useWPPage(slug, lang);
  const visuallyHiddenSx = {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0
  };

  const { heroHtml, contentHtml } = useMemo(() => {
    if (!page?.content?.rendered) {
      return { heroHtml: "", contentHtml: "" };
    }
    if (typeof window === "undefined") {
      return { heroHtml: "", contentHtml: page.content.rendered };
    }
    const doc = new window.DOMParser().parseFromString(page.content.rendered, "text/html");
    doc.querySelectorAll("a").forEach((anchor) => {
      if (!anchor.getAttribute("href")) {
        const rel = anchor.getAttribute("rel");
        if (rel && rel.startsWith("/")) {
          anchor.setAttribute("href", rel);
        }
      }
    });
    const heroElement = doc.querySelector(".hero");
    let extractedHero = "";
    if (heroElement) {
      extractedHero = heroElement.innerHTML;
      heroElement.remove();
    }
    return { heroHtml: extractedHero, contentHtml: doc.body.innerHTML };
  }, [page?.content?.rendered]);

  if (loading) return <Loading />;
  if (notFound || !page) return <NotFound />;

  const featuredImage =
    page._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
    page._embedded?.["wp:featuredmedia"]?.[0]?.media_details?.sizes?.large?.source_url ||
    page._embedded?.["wp:featuredmedia"]?.[0]?.media_details?.sizes?.full?.source_url;

  const showHeroBlock = heroHtml.trim().length > 0;
  const showHeroImage = !showHeroBlock && Boolean(featuredImage);
  const titleInHero = showHeroBlock || showHeroImage;
  const fallbackDescription = page.excerpt?.rendered || "";
  const acf = page.acf || {};
  const seoTitle = acf.seo_title || page.title.rendered;
  const seoDescription = acf.seo_description || fallbackDescription;
  const ogTitle = acf.og_title || seoTitle;
  const ogDescription = acf.og_description || seoDescription;
  const ogImage = acf.og_image || featuredImage || "";
  const canonicalUrl = acf.canonical_url || (typeof window !== "undefined" ? window.location.href : "");
  const isLockedPage = !isContentVisibleForAccess(page, []);
  const lockedVisibility = getContentVisibilityLevel(page);
  const robots = acf.robots || (isLockedPage ? "noindex" : "index");
  const showH1 = acf.show_h1 ?? true;
  const hideH1Visually = acf.hide_h1_visually ?? false;
  const renderVisibleH1 = showH1 && !hideH1Visually;
  const renderHiddenH1 = hideH1Visually;
  const showContactForm = !isLockedPage && isContactPage(page);
  const showNewsletterSignup = !isLockedPage && isNewsletterPage(page);

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        ogTitle={ogTitle}
        ogDescription={ogDescription}
        ogImage={ogImage}
        canonicalUrl={canonicalUrl}
        robots={robots}
      />
      {showHeroBlock ? (
        <Box
          component="section"
          sx={{
            py: { xs: 6, md: 10 },
            background: "linear-gradient(120deg, rgba(15, 143, 142, 0.12) 0%, rgba(13, 54, 64, 0.08) 100%)"
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 720 }}>
              {renderVisibleH1 ? (
                <Typography
                  variant="h1"
                  component="h1"
                  dangerouslySetInnerHTML={{ __html: page.title.rendered }}
                />
              ) : null}
              <Box
                sx={{
                  mt: 2,
                  "& h2": { mt: 2.5, mb: 1.5, fontWeight: 700 },
                  "& h3": { mt: 2, mb: 1, fontWeight: 700 },
                  "& p": { mb: 2, color: "text.secondary" },
                  "& a": { color: "primary.main", fontWeight: 600 },
                  "& ul, & ol": { pl: 3, mb: 2, color: "text.secondary" },
                  "& .wp-block-columns": {
                    display: "grid",
                    gap: { xs: 2, md: 3 },
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }
                  },
                  "& .wp-block-columns .wp-block-column": { minWidth: 0 },
                  "& .wp-block-columns.has-3-columns": {
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }
                  },
                  "& .wp-block-buttons": { display: "flex", gap: 1.5, flexWrap: "wrap", mt: 2 },
                  "& .wp-block-button__link": {
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "10px 20px",
                    borderRadius: 12,
                    backgroundColor: "primary.main",
                    color: "common.white"
                  },
                  "& img": { maxWidth: "100%", height: "auto", display: "block", borderRadius: 2 }
                }}
                dangerouslySetInnerHTML={{ __html: heroHtml }}
              />
            </Box>
          </Container>
        </Box>
      ) : null}
      {showHeroImage ? (
        <Box
          component="section"
          sx={{
            position: "relative",
            py: { xs: 7, md: 11 },
            color: "common.white",
            backgroundImage: `url(${featuredImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(120deg, rgba(13, 54, 64, 0.75) 0%, rgba(11, 31, 39, 0.55) 100%)"
            }}
          />
          <Container maxWidth="lg" sx={{ position: "relative" }}>
            {renderVisibleH1 ? (
              <Typography
                variant="h1"
                component="h1"
                dangerouslySetInnerHTML={{ __html: page.title.rendered }}
              />
            ) : null}
          </Container>
        </Box>
      ) : null}
      <Box component="main" sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          {renderHiddenH1 ? (
            <Typography
              variant="h1"
              component="h1"
              sx={visuallyHiddenSx}
              dangerouslySetInnerHTML={{ __html: page.title.rendered }}
            />
          ) : null}
          {!titleInHero && renderVisibleH1 ? (
            <Typography
              variant="h1"
              component="h1"
              dangerouslySetInnerHTML={{ __html: page.title.rendered }}
            />
          ) : null}
          {isLockedPage ? (
            <Box sx={{ mt: titleInHero ? 0 : 3, display: "grid", gap: 2 }}>
              <Chip
                icon={<LockOutlinedIcon />}
                label={lockedVisibility === "private" ? "Privater Inhalt" : "Premium Inhalt"}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ width: "fit-content" }}
              />
              <Box
                sx={{
                  p: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  bgcolor: "background.paper"
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {acf.access_teaser_message || "Dieser Inhalt ist Teil eines geschuetzten Bereichs und wird erst nach Freischaltung sichtbar."}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              component="article"
              sx={{
                mt: titleInHero ? 0 : 3,
                "& h2": { mt: 3, mb: 1.5, fontWeight: 700 },
                "& h3": { mt: 2.5, mb: 1, fontWeight: 700 },
                "& p": { mb: 2, color: "text.secondary" },
                "& a": { color: "primary.main", fontWeight: 600 },
                "& ul, & ol": { pl: 3, mb: 2, color: "text.secondary" },
                "& img": { maxWidth: "100%", height: "auto", display: "block", borderRadius: 2 },
                "& .wp-block-columns": {
                  display: "grid",
                  gap: { xs: 2, md: 3 },
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }
                },
                "& .wp-block-columns .wp-block-column": { minWidth: 0 },
                "& .wp-block-columns.has-3-columns": {
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }
                },
                "& .wp-block-buttons": { display: "flex", gap: 1.5, flexWrap: "wrap", mt: 2 },
                "& .wp-block-button__link": {
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "10px 20px",
                  borderRadius: 12,
                  backgroundColor: "primary.main",
                  color: "common.white"
                }
              }}
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />
          )}
          {showContactForm ? <ContactForm lang={lang} page={page} /> : null}
          {showNewsletterSignup ? <NewsletterSignup lang={lang} page={page} /> : null}
        </Container>
      </Box>
    </>
  );
}
